import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';

import { AuthProvider, useAuth } from '@/lib/auth';
import { BrandingProvider } from '@/lib/branding';
import { VenueProvider } from '@/lib/venue';
import { ToastProvider } from '@/components/ui/Toast';
import { RequireAuth } from '@/components/RequireAuth';

import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import VerifyEmail from '@/pages/VerifyEmail';
import AdminLogin from '@/pages/AdminLogin';

import Privacy from '@/pages/Privacy';
import Terms from '@/pages/Terms';
import Support from '@/pages/Support';
import RefundPolicy from '@/pages/RefundPolicy';

import CustomerLayout from '@/components/layout/CustomerLayout';
import AdminLayout from '@/components/layout/AdminLayout';

import MyBookings from '@/pages/customer/MyBookings';
import BookCourt from '@/pages/customer/BookCourt';
import Memberships from '@/pages/customer/Memberships';
import Venues from '@/pages/customer/Venues';
import VenueDetails from '@/pages/customer/VenueDetails';

import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminPlatformStats from '@/pages/admin/AdminPlatformStats';
import AdminVenues from '@/pages/admin/AdminVenues';
import AdminVenueProfile from '@/pages/admin/AdminVenueProfile';
import AdminSettings from '@/pages/admin/AdminSettings';
import AdminBookings from '@/pages/admin/AdminBookings';
import AdminUsers from '@/pages/admin/AdminUsers';
import AdminPayments from '@/pages/admin/AdminPayments';
import AdminMembers from '@/pages/admin/AdminMembers';
import AdminCourts from '@/pages/admin/AdminCourts';
import AdminPlans from '@/pages/admin/AdminPlans';
import AdminSlotBlocks from '@/pages/admin/AdminSlotBlocks';
import AdminCustomers from '@/pages/admin/AdminCustomers';
import AdminOffers from '@/pages/admin/AdminOffers';
import AdminSports from '@/pages/admin/AdminSports';
import AdminFeed from '@/pages/admin/AdminFeed';
import AdminBilling from '@/pages/admin/AdminBilling';
import AdminInvoices from '@/pages/admin/AdminInvoices';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import CompleteProfile from '@/pages/CompleteProfile';

// Role-aware landing page inside /admin so any role that reaches the bare
// "/admin" URL (bookmark, back button, or a generic login redirect) lands
// on a page it is actually allowed to see, instead of always going to
// "dashboard" (which operators cannot access and would bounce them home).
// Phase 4: super_admin no longer has dashboard access either — restricted
// to venue creation + staff assignment. Phase 5 gives them their own
// platform-wide dashboard to land on instead.
const AdminIndexRedirect = () => {
  const { user } = useAuth();
  let target = 'dashboard';
  if (user?.role === 'operator') target = 'bookings';
  else if (user?.role === 'super_admin') target = 'platform';
  return <Navigate to={target} replace />;
};

export default function App() {

  return (
    <BrandingProvider>
    <AuthProvider>
    <VenueProvider>

      <ToastProvider>

        <BrowserRouter>

          <Routes>

            {/* PUBLIC ROUTES */}

            <Route
              path="/"
              element={<Landing />}
            />

            <Route
              path="/login"
              element={<Login />}
            />

            <Route
              path="/register"
              element={<Register />}
            />

            <Route
              path="/verify-email"
              element={<VerifyEmail />}
            />

            <Route
  path="/forgot-password"
  element={<ForgotPassword />}
/>

<Route
  path="/reset-password"
  element={<ResetPassword />}
/>

            <Route
              path="/admin/login"
              element={<AdminLogin />}
            />

            <Route
              path="/privacy"
              element={<Privacy />}
            />

            <Route
              path="/terms"
              element={<Terms />}
            />

            <Route
              path="/support"
              element={<Support />}
            />

            <Route
              path="/refund-policy"
              element={<RefundPolicy />}
            />

            {/* CUSTOMER ROUTES */}

            <Route
              path="/app"
              element={
                <RequireAuth roles={['customer']}>
                  <CustomerLayout />
                </RequireAuth>
              }
            >
              <Route
  path="complete-profile"
  element={<CompleteProfile />}
/>

              <Route
                index
                element={<Navigate to="bookings" replace />}
              />

              <Route
                path="bookings"
                element={<MyBookings />}
              />

              <Route
                path="book"
                element={<BookCourt />}
              />

              <Route
                path="memberships"
                element={<Memberships />}
              />

              <Route
                path="venues"
                element={<Venues />}
              />

              <Route
                path="venues/:venueId"
                element={<VenueDetails />}
              />

            </Route>

            {/* ADMIN ROUTES */}

            <Route
              path="/admin"
              element={
                <RequireAuth
                  roles={[
                    'super_admin',
                    'admin',
                    'operator'
                  ]}
                >
                  <AdminLayout />
                </RequireAuth>
              }
            >

              <Route
                index
                element={<AdminIndexRedirect />}
              />

              {/* SUPER ADMIN ONLY: platform-wide dashboard (Phase 5) */}

              <Route
                path="platform"
                element={
                  <RequireAuth roles={['super_admin']}>
                    <AdminPlatformStats />
                  </RequireAuth>
                }
              />

              {/* VENUE ADMIN ONLY */}

              <Route
                path="dashboard"
                element={
                  <RequireAuth
                    roles={['admin']}
                  >
                    <AdminDashboard />
                  </RequireAuth>
                }
              />

              {/* SUPER ADMIN ONLY */}

              <Route
                path="venues"
                element={
                  <RequireAuth
                    roles={['super_admin']}
                  >
                    <AdminVenues />
                  </RequireAuth>
                }
              />

              <Route
                path="settings"
                element={
                  <RequireAuth
                    roles={['super_admin']}
                  >
                    <AdminSettings />
                  </RequireAuth>
                }
              />

              {/* SUPER ADMIN + VENUE ADMIN (admin sees only their own operators) */}

              <Route
                path="users"
                element={
                  <RequireAuth
                    roles={['super_admin', 'admin']}
                  >
                    <AdminUsers />
                  </RequireAuth>
                }
              />

              {/* VENUE ADMIN + OPERATOR (Phase 4: day-to-day venue operations, not super_admin) */}

              <Route
                path="bookings"
                element={
                  <RequireAuth roles={['admin', 'operator']}>
                    <AdminBookings />
                  </RequireAuth>
                }
              />

              <Route
                path="customers"
                element={
                  <RequireAuth roles={['admin', 'operator']}>
                    <AdminCustomers />
                  </RequireAuth>
                }
              />

              <Route
                path="members"
                element={
                  <RequireAuth roles={['admin', 'operator']}>
                    <AdminMembers />
                  </RequireAuth>
                }
              />

              {/* VENUE ADMIN ONLY */}

              <Route
                path="payments"
                element={
                  <RequireAuth
                    roles={['admin']}
                  >
                    <AdminPayments />
                  </RequireAuth>
                }
              />

              <Route
                path="courts"
                element={
                  <RequireAuth
                    roles={['admin']}
                  >
                    <AdminCourts />
                  </RequireAuth>
                }
              />

              <Route
                path="plans"
                element={
                  <RequireAuth
                    roles={['admin']}
                  >
                    <AdminPlans />
                  </RequireAuth>
                }
              />

              <Route
                path="blocks"
                element={
                  <RequireAuth
                    roles={['admin']}
                  >
                    <AdminSlotBlocks />
                  </RequireAuth>
                }
              />

              <Route
                path="venue-profile"
                element={
                  <RequireAuth roles={['admin']}>
                    <AdminVenueProfile />
                  </RequireAuth>
                }
              />

              <Route
                path="offers"
                element={
                  <RequireAuth roles={['super_admin', 'admin']}>
                    <AdminOffers />
                  </RequireAuth>
                }
              />

              <Route
                path="sports"
                element={
                  <RequireAuth roles={['super_admin']}>
                    <AdminSports />
                  </RequireAuth>
                }
              />

              <Route
                path="feed"
                element={
                  <RequireAuth roles={['super_admin']}>
                    <AdminFeed />
                  </RequireAuth>
                }
              />

              {/* Platform-fee billing: venue admin pays, super admin collects */}

              <Route
                path="billing"
                element={
                  <RequireAuth roles={['admin']}>
                    <AdminBilling />
                  </RequireAuth>
                }
              />

              <Route
                path="invoices"
                element={
                  <RequireAuth roles={['super_admin']}>
                    <AdminInvoices />
                  </RequireAuth>
                }
              />

            </Route>

            {/* FALLBACK */}

            <Route
              path="*"
              element={<Navigate to="/" replace />}
            />

          </Routes>

        </BrowserRouter>

      </ToastProvider>

    </VenueProvider>
    </AuthProvider>
    </BrandingProvider>
  );
}